import type { Translator } from '@/types/translator';

import type { TargetLanguage } from '../types';

export interface TranslateWordCloudParams {
  keywords: string[];
  targetLanguage: TargetLanguage;
  detectLanguage: (
    text: string,
  ) => Promise<{ detectedLanguage: string } | null>;
}

export interface TranslateWordCloudResult {
  /** Map of keyword → translated text */
  translations: Map<string, string>;
  /** Map of keyword → detected source language */
  detectedLanguages: Map<string, string>;
}

/**
 * Checks if the Web Translator API is supported
 */
export const isTranslatorSupported = (): boolean => {
  return typeof window !== 'undefined' && 'Translator' in window;
};

export const inferFallbackSourceLanguage = (
  text: string,
  targetLanguage: TargetLanguage,
): 'en' | 'ko' | null => {
  const hasHangul = /[\u3131-\u318E\uAC00-\uD7A3]/.test(text);
  const hasLatin = /[A-Za-z]/.test(text);

  if (targetLanguage === 'ko' && hasLatin) {
    return 'en';
  }

  if (targetLanguage === 'en' && hasHangul) {
    return 'ko';
  }

  return null;
};

/**
 * Pure function to translate word cloud keywords
 * Detects source language for each keyword and translates to target language
 */
export async function translateWordCloud({
  keywords,
  targetLanguage,
  detectLanguage,
}: TranslateWordCloudParams): Promise<TranslateWordCloudResult> {
  if (keywords.length === 0) {
    return {
      translations: new Map(),
      detectedLanguages: new Map(),
    };
  }

  // Step 1: Detect language for each keyword
  const detections = await Promise.all(
    keywords.map(async (keyword) => {
      const result = await detectLanguage(keyword);

      console.log('[translateWordCloud] detectLanguage', {
        keywordPreview: keyword.slice(0, 120),
        keywordLength: keyword.length,
        detectedLanguage: result?.detectedLanguage || 'ko',
      });

      return {
        keyword,
        sourceLang: result?.detectedLanguage || 'ko', // fallback to Korean
      };
    }),
  );

  const detectedLanguages = new Map<string, string>();
  detections.forEach(({ keyword, sourceLang }) => {
    detectedLanguages.set(keyword, sourceLang);
  });

  // Step 2: Group keywords by source language (skip if source === target)
  const keywordsByLang = new Map<string, string[]>();
  const translations = new Map<string, string>();

  detections.forEach(({ keyword, sourceLang }) => {
    if (sourceLang === targetLanguage) {
      // No translation needed, use original
      translations.set(keyword, keyword);
      return;
    }

    const existing = keywordsByLang.get(sourceLang) || [];
    existing.push(keyword);
    keywordsByLang.set(sourceLang, existing);
  });

  // If all keywords are already in target language, return early
  if (keywordsByLang.size === 0) {
    return { translations, detectedLanguages };
  }

  // Step 3: Translate each language group
  const createdTranslators: Translator[] = [];

  try {
    await Promise.all(
      Array.from(keywordsByLang.entries()).map(
        async ([sourceLang, groupKeywords]) => {
          try {
            let resolvedSourceLang = sourceLang;

            // Check translator availability for this language pair
            let availability = await window.Translator!.availability({
              sourceLanguage: resolvedSourceLang,
              targetLanguage,
            });

            console.log('[translateWordCloud] availability', {
              sourceLang: resolvedSourceLang,
              targetLanguage,
              availability,
              groupKeywords: groupKeywords.map((keyword) => keyword.slice(0, 120)),
            });

            if (availability === 'unavailable' || availability === undefined) {
              const fallbackSourceLang = inferFallbackSourceLanguage(
                groupKeywords[0] ?? '',
                targetLanguage,
              );

              if (
                fallbackSourceLang &&
                fallbackSourceLang !== resolvedSourceLang
              ) {
                console.warn('[translateWordCloud] retry with fallback source language', {
                  sourceLang: resolvedSourceLang,
                  fallbackSourceLang,
                  targetLanguage,
                });

                resolvedSourceLang = fallbackSourceLang;
                availability = await window.Translator!.availability({
                  sourceLanguage: resolvedSourceLang,
                  targetLanguage,
                });

                console.log('[translateWordCloud] fallback availability', {
                  sourceLang: resolvedSourceLang,
                  targetLanguage,
                  availability,
                });
              }
            }

            if (availability === 'unavailable' || availability === undefined) {
              console.error('[translateWordCloud] unavailable language options', {
                sourceLang,
                resolvedSourceLang,
                targetLanguage,
                availability,
              });
              groupKeywords.forEach((k) => translations.set(k, k));
              return;
            }

            // Create translator for this language pair
            let translator: Translator;

            try {
              translator = await window.Translator!.create({
                sourceLanguage: resolvedSourceLang,
                targetLanguage,
              });
            } catch (error) {
              console.error('[translateWordCloud] create failed', {
                sourceLang: resolvedSourceLang,
                targetLanguage,
                error,
              });
              throw error;
            }

            createdTranslators.push(translator);

            // Translate all keywords in this group
            const results = await Promise.all(
              groupKeywords.map(async (keyword) => {
                try {
                  const translated = await translator.translate(keyword);

                  console.log('[translateWordCloud] translate success', {
                    sourceLang: resolvedSourceLang,
                    targetLanguage,
                    keywordPreview: keyword.slice(0, 120),
                    keywordLength: keyword.length,
                    translatedPreview: translated.slice(0, 120),
                    translatedLength: translated.length,
                  });

                  return translated;
                } catch (error) {
                  console.error('[translateWordCloud] translate failed', {
                    sourceLang: resolvedSourceLang,
                    targetLanguage,
                    keywordPreview: keyword.slice(0, 120),
                    keywordLength: keyword.length,
                    error,
                  });
                  return keyword; // fallback to original on error
                }
              }),
            );

            // Store results
            groupKeywords.forEach((keyword, index) => {
              translations.set(keyword, results[index]);
            });
          } catch (error) {
            // On error, keep original keywords for this language group
            console.error('[translateWordCloud] group failed', {
              sourceLang,
              targetLanguage,
              groupKeywords: groupKeywords.map((keyword) => keyword.slice(0, 120)),
              error,
            });
            groupKeywords.forEach((k) => translations.set(k, k));
          }
        },
      ),
    );
  } finally {
    // Cleanup created translators
    createdTranslators.forEach((translator) => {
      try {
        translator.destroy();
      } catch {
        // Ignore cleanup errors
      }
    });
  }

  return { translations, detectedLanguages };
}
